export const DEFAULT_LEVEL_LABELS = ['Topic', 'Subtopic', 'Chapter', 'Section'];
export const MAX_HIERARCHY_DEPTH = 4;

export interface SubjectDetail {
  id: string;
  title: string;
  image: string | null;
  level_labels: string[];
  teachers: Array<{
    id: string;
    first_name: string;
    last_name: string;
    profile_photo?: string | null;
  }>;
  created_at?: string;
  updated_at?: string;
}

export interface HierarchyItem {
  id: string;
  subject_id: string;
  level: number;
  name: string;
  parent_id: string | null;
  parent_name: string | null;
  child_count: number;
  created_at?: string;
  updated_at?: string;
}

/** Normalize a raw API subject payload into a SubjectDetail. */
export const toSubjectDetail = (raw: any): SubjectDetail => ({
  id: String(raw?.id ?? ''),
  title: raw?.title ?? '',
  image: raw?.image ?? null,
  level_labels: normalizeLabels(raw?.level_labels),
  teachers: Array.isArray(raw?.teachers) ? raw.teachers : [],
  created_at: raw?.created_at,
  updated_at: raw?.updated_at,
});

export const normalizeLabels = (labels?: string[]): string[] => {
  const result = [...DEFAULT_LEVEL_LABELS];
  if (Array.isArray(labels)) {
    for (let i = 0; i < MAX_HIERARCHY_DEPTH; i += 1) {
      if (labels[i] !== undefined && labels[i] !== null) {
        result[i] = String(labels[i]);
      }
    }
  }
  return result;
};

/** Number of usable levels (a blank label disables that level and deeper). */
export const enabledDepth = (labels: string[]): number => {
  let count = 0;
  for (let i = 0; i < MAX_HIERARCHY_DEPTH; i += 1) {
    if (labels[i] && labels[i].trim()) {
      count += 1;
    } else {
      break;
    }
  }
  return count;
};

export const labelAt = (labels: string[], level: number): string =>
  labels[level - 1] || `Level ${level}`;

export const pluralize = (label: string): string => {
  const trimmed = (label || 'Item').trim();
  return /s$/i.test(trimmed) ? trimmed : `${trimmed}s`;
};

/**
 * Validate the 4 level labels. Returns a per-index error array (empty strings
 * where valid). First two required; enabled levels must form a contiguous
 * prefix (no enabled level below a blank one).
 */
export const validateLevelLabels = (labels: string[]): string[] => {
  const errors = ['', '', '', ''];
  const clean = labels.map(l => (l || '').trim());

  if (!clean[0]) errors[0] = 'Required';
  if (!clean[1]) errors[1] = 'Required';

  let seenBlank = false;
  for (let i = 0; i < MAX_HIERARCHY_DEPTH; i += 1) {
    if (!clean[i]) {
      seenBlank = true;
    } else if (seenBlank && !errors[i]) {
      errors[i] = 'Fill earlier levels first';
    }
  }
  return errors;
};

export const hasLabelErrors = (errors: string[]): boolean =>
  errors.some(e => !!e);
